import {_} from 'lodash';
import * as ko from "knockout"
import {
    IButton,
    ICompany,
    ICompanySyncstep,
    IMessage,
    IMessageData,
    IMessageType,
    IObjectData,
    IRuleset,
    ISystem,
    ISystemRole
} from "../../../../index"
import {createErrorMessage, getRawSyncFieldValue, isPartOf, validateEmail} from "../../../../util/misc";
import {KOSubscriptionTracker} from "../../../../util/KOSubscriptionTracker";
import {PLEASE_WAIT_MESSAGE} from "../../../../constants/Language";
import {ButtonManager} from "../../../../util/ButtonManager";
import PromiseRequest from "../../../../req/PromiseRequest";
import {getSearchSystemParams} from "../../../../util/request";
import {
    DEFAULT_SMTP_SENDER,
    HIERARCHY_ARROW,
    MAX_RESULTS_PER_PAGE,
    ObjectTypeId,
    TEXT_WRAPPER_END,
    TEXT_WRAPPER_START
} from "../../../../constants";
import {global, lm} from "../../../../appContext";
import {
    getCancelButton,
    getExitButton,
    getHash,
    getNextButton,
    getPreviousButton,
    getSubmitButton,
    setRouteHash
} from "../../../../constants/RouteDefinitions";
import {IDialogPickObjectParams} from "../../../object/dialog/pick";

import {nsRuleset} from "../../../ruleset/nsRuleset";
import {nsGenerateRsAndMf, nsGenerateRsAndMf as ns} from "./nsGenerateRsAndMf";
import {IResultObject, TTranslationKeyPrefix} from "./index";
import {nsMessages} from "../../../messages/fn";
import {ImportFormatId} from "../../../../constants/ImportFormats";
import RecipientType = ns.RecipientType;
import Step = ns.Step;
import EventType = nsGenerateRsAndMf.EventType;
import mainHtml from './main.html?raw';

export let template = mainHtml

export class viewModel {

    private readonly Step = Step
    private readonly step: KnockoutObservable<ns.Step | null> = ko.observable(null)

    private readonly EventDefinitions = ns.EventDefinitions

    private readonly RecipientType = RecipientType
    private readonly RecipientDefinitions = ns.RecipientDefinitions


    private readonly messageData: KnockoutObservable<IMessageData | null> = ko.observable(null)
    private readonly kosub: KOSubscriptionTracker = new KOSubscriptionTracker()
    private readonly buttonManager = new ButtonManager()

    private readonly req_createmessage: PromiseRequest = new PromiseRequest('createmessage')
    private readonly req_createruleset: PromiseRequest = new PromiseRequest('createruleset')
    private readonly req_getcompany: PromiseRequest = new PromiseRequest('getcompany')
    private readonly req_getcompanysyncsteps: PromiseRequest = new PromiseRequest('getcompanysyncsteps')
    private readonly req_getmessagetypes: PromiseRequest = new PromiseRequest('getmessagetypes')
    private readonly req_searchsystem: PromiseRequest = new PromiseRequest('searchsystem')

    private readonly pageButtons: ButtonManager = new ButtonManager()

    private data_allSystems!: ISystem[]
    private data_allMessageTypes!: IMessageType[]
    private data_allCompanySyncsteps!: ICompanySyncstep[]
    private data_company!: ICompany

    private data_system!: ISystem
    private data_systemRole!: ISystemRole

    private readonly allEventTypes: ns.EventType[] = [EventType.Add, EventType.Delete, EventType.Update]
    private allRecipients: RecipientType[] = [RecipientType.Employee, RecipientType.Manager, RecipientType.SystemOwner, RecipientType.Custom]
    private readonly event: KnockoutObservable<ns.EventType> = ko.observable<ns.EventType>(EventType.Add)
    private readonly recipient: KnockoutObservable<RecipientType> = ko.observable<RecipientType>(RecipientType.Employee)
    private readonly recipientEmail: KnockoutObservable<string | null> = ko.observable(null)
    private readonly target: KnockoutObservable<ISystem | ISystemRole | null> = ko.observable(null);
    private readonly targetName: KnockoutObservable<string | null> = ko.observable(null);

    private readonly results: KnockoutObservableArray<IResultObject> = ko.observableArray()

    private readonly createRuleset: IResultObject
    private readonly createMessageFlow: IResultObject


    constructor() {
        const obj = (): IResultObject => ({
            data: null,
            error: ko.observable<string | null>(null),
            icon: ko.observable<string | null>(null),
            label: ko.observable<string | null>(null),
            link: ko.observable<string | null>(null),
            objIcon: null,
            spinner: ko.observable(false),
        })
        this.createRuleset = obj()
        this.createMessageFlow = obj()

        this.createRuleset.label('Ruleset|c')
        this.createMessageFlow.label('Message flow|c')

        this.createRuleset.objIcon = 'fa-ruleset'
        this.createMessageFlow.objIcon = 'fa-message-flow'

        this.results.push(this.createRuleset)
        this.results.push(this.createMessageFlow)

        this.kosub.subscribe(this.step, step => {
            if (step != null) {
                this.onStepChange(step)
            }
        })
        this.initButtons()
        this.step(Step.Init)
    }

    private initButtons() {
        const systemId = () => Number(global.router.currentRoute()?.__routeParams?.systemid)
        const btn_abort = getCancelButton(() => setRouteHash('systemaccessdetails', {id: systemId()}), {
            validator: () => this.isStep(Step.Summary, Step.Process),
        })
        this.pageButtons.add(btn_abort)
        const btn_exit = getExitButton(() => setRouteHash('systemaccessdetails', {id: systemId()}), {
            validator: () => this.isStep(Step.Finish),
        })
        this.pageButtons.add(btn_exit)
        const btn_prev: IButton = getPreviousButton({
            onClick: () => {
                const step = this.step()
                if (step != null) {
                    this.step(step - 1)
                }
            },
            enabled: () => !this.isStep(Step.Event),
            validator: () => this.isStep(Step.Event, Step.Recipient, Step.Summary),
        })
        this.pageButtons.add(btn_prev)
        const btn_next: IButton = getNextButton({
            onClick: () => {
                const step = this.step()
                if (step != null) {
                    this.step(step + 1)
                }
            },
            enabled: () => this.isFormValid(),
            validator: () => this.isStep(Step.Event, Step.Recipient),
        })
        this.pageButtons.add(btn_next)
        const btn_submit: IButton = getSubmitButton({
            onClick: () => {
                const step = this.step()
                if (step != null) {
                    this.step(step + 1)
                }
            },
            validator: () => this.isStep(Step.Summary),
        })
        this.pageButtons.add(btn_submit)
    }

    private isFormValid(): boolean {
        switch (this.step()) {
            case Step.Event: {
                return !!this.event()
            }
            case Step.Recipient: {
                if (this.recipient() == RecipientType.Custom) {
                    return this.isRecipientEmailValid()
                }
                return true
            }
            default: {
                return true
            }
        }
    }

    private isRecipientEmailValid(): boolean {
        const email = this.recipientEmail()
        if (!email) {
            return false
        }
        return nsMessages
            .splitRecipients(email)
            .every(str => {
                const a = validateEmail(str)
                const b = str.startsWith(TEXT_WRAPPER_START) && str.endsWith(TEXT_WRAPPER_END)
                return a || b
            })
    }

    private isStep(...step: ns.Step[]): boolean {
        return isPartOf(this.step(), ...step)
    }

    private onStepChange(step: ns.Step) {
        this.messageData(null)
        switch (step) {
            case Step.Init: {
                this.messageData({message: PLEASE_WAIT_MESSAGE})
                Promise.resolve()
                    .then(() => this.req_searchsystem
                        .request(getSearchSystemParams({
                            resultsprpage: MAX_RESULTS_PER_PAGE
                        }))
                        .then((data: ISystem[]) => {
                            this.data_allSystems = data
                            const routeParams = global.router.currentRoute()?.__routeParams
                            const systemId = Number(routeParams?.systemid)
                            const systemRoleId = Number(routeParams?.['systemroleid'])
                            let name: string[] = []
                            if (systemId) {
                                const system = data.find(o => o.id == systemId)
                                if (system) {
                                    this.data_system = system
                                    this.target(system)
                                    name.push(system.name)
                                }
                            }
                            if (systemRoleId && this.data_system) {
                                const role = this.data_system.roles.find(o => o.id == systemRoleId)
                                if (role) {
                                    this.data_systemRole = role
                                    this.target(role)
                                    name.push(role.name)
                                }
                            }
                            this.targetName(name.join(HIERARCHY_ARROW))
                        })
                    )
                    .then(() => this.req_getmessagetypes
                        .request()
                        .then((data: IMessageType[]) => this.data_allMessageTypes = data)
                    )
                    .then(() => this.req_getcompanysyncsteps
                        .request()
                        .then((data: ICompanySyncstep[]) => this.data_allCompanySyncsteps = data)
                    )
                    .then(() => this.req_getcompany
                        .request()
                        .then((data: [ICompany]) => this.data_company = data[0])
                    )
                    .then(() => {
                        this.step(Step.Event)
                    })
                    .catch((reason: string) => {
                        this.messageData(createErrorMessage(String(reason)))
                    })
                break
            }
            case Step.Event: {
                break
            }
            case Step.Recipient: {
                break
            }
            case Step.Summary: {
                this.getSubmitData_ruleset()
                this.getSubmitData_messageFlow()
                break
            }
            case Step.Process: {
                Promise.resolve()
                    .then(() => {
                        this.createRuleset.spinner(true)
                        const submitData = this.getSubmitData_ruleset()
                        const jruleset = JSON.stringify(submitData)
                        return this.req_createruleset
                            .request({jruleset})
                            .then((data: [IRuleset]) => {
                                this.createRuleset.data = data[0]
                                this.createRuleset.link(getHash('rulesetdetails', this.createRuleset.data))
                                this.createRuleset.spinner(false)
                                this.createRuleset.icon('fa-check text-success')
                            })
                            .catch((reason: string) => {
                                this.createRuleset.icon('fa-times-circle text-danger')
                                this.createRuleset.error(reason)
                                this.step(Step.Finish)
                            })
                    })
                    .then(() => {
                        this.createMessageFlow.spinner(true)
                        const submitData = this.getSubmitData_messageFlow()
                        submitData.body = encodeURIComponent(submitData.body)
                        const jmessage = JSON.stringify(submitData);
                        return this.req_createmessage
                            .request({jmessage})
                            .then((data: [IMessage]) => {
                                this.createMessageFlow.data = data[0]
                                this.createMessageFlow.link(getHash('messageflowdetails', this.createMessageFlow.data))
                                this.createMessageFlow.spinner(false)
                                this.createMessageFlow.icon('fa-check text-success')
                            })
                            .catch((reason: string) => {
                                this.createMessageFlow.icon('fa-times-circle text-danger')
                                this.createMessageFlow.error(reason)
                                this.step(Step.Finish)
                            })
                    })
                    .then(() => {
                        global.cacheManager.removeCache('getruleset')
                        global.cacheManager.removeCache('getmessage')
                        global.cacheManager.removeCache('searchruleset')
                        global.cacheManager.removeCache('searchmessage')
                        this.step(Step.Finish)
                    })
                    .catch((reason: string) => this.messageData(createErrorMessage(String(reason))))
                break
            }
        }
    }

    private onPickUserClick() {
        const getMail = (o: IObjectData) => getRawSyncFieldValue(['Email', 'Mail'], o.syncfields);
        const fnValidateRow = (o: IObjectData) => !!getMail(o);
        global.dialogManager.addComponent({
            title: lm.t(`choose subject|s:((user|ac))`) ?? '',
            content: {
                name: 'component/object/dialog/pick',
                params: {
                    fnValidateRow: fnValidateRow,
                    getRowBindings: (o: IObjectData) => {
                        const isValid = fnValidateRow(o);
                        return {
                            cls: () => isValid ? '' : 'disabled',
                            attr: {
                                title: isValid ? getMail(o) : lm.t('user has no registered email'),
                                tabindex: isValid ? 0 : -1,
                            },
                        }
                    },
                    objecttypeid: ObjectTypeId.User,
                    onObjectSelected: (o: IObjectData) => this.recipientEmail(getMail(o) ?? null),
                    syncfieldNames: ['Email', 'Mail'],
                } as IDialogPickObjectParams,
            }
        });
    }

    private getLabel(key: string): string {
        const translationObj = this.getTranslationObj()
        return lm.t({
            k: this.getTranslationPrefix() + '.' + key,
            obj: translationObj,
        }) ?? ''
    }

    private getSubmitData_messageFlow(): IMessage {
        if (this.createMessageFlow?.data) {
            return this.createMessageFlow.data as IMessage
        }
        const recipientDefinition = ns.RecipientDefinitions[this.recipient()]
        const messagetype = this.data_allMessageTypes.find(o => o.id == recipientDefinition.messageTypeId)
        if (!messagetype) {
            throw new Error('Missing message type for recipient definition')
        }
        const ruleset = this.getSubmitData_ruleset()
        const lastCompanySyncstep = _(this.data_allCompanySyncsteps).sortBy((o: ICompanySyncstep) => o.order).last()
        if (!lastCompanySyncstep) {
            throw new Error('Missing company syncstep')
        }

        let addrecipient: string
        switch (this.recipient()) {
            case RecipientType.Custom: {
                addrecipient = this.recipientEmail() ?? ''
                break
            }
            case RecipientType.SystemOwner: {
                addrecipient = `[SYSTEMOWNER]`
                break
            }
            default: {
                addrecipient = ''
                break
            }
        }

        return {
            active: false,
            addrecipient: addrecipient,
            body: this.getLabel('messageflow.body'),
            companyid: String(global.login.selectedOrganizationId() ?? ''),
            description: this.getLabel('messageflow.description'),
            id: 0,
            logcount: 0,
            messagelog: [],
            messagetype: messagetype,
            messagetypeid: messagetype.id,
            messagetypename: messagetype.name,
            name: this.getLabel('messageflow.title'),
            recipientfield: "Email",
            rulesetid: ruleset.id,
            rulesetname: ruleset.name,
            sender: this.data_company.defaultsmtpsender || DEFAULT_SMTP_SENDER,
            subject: this.getLabel('messageflow.subject'),
            syncstepid: lastCompanySyncstep.syncstep.id,
            syncstepname: lastCompanySyncstep.syncstep.name,
        }
    }

    private getSubmitData_ruleset(): IRuleset {
        if (this.createRuleset?.data) {
            return this.createRuleset.data as IRuleset
        }

        const targetValue = (() => {
            switch (this.event()) {
                case nsGenerateRsAndMf.EventType.Delete:
                    return '[OldValue]'
                case nsGenerateRsAndMf.EventType.Add:
                case nsGenerateRsAndMf.EventType.Update:
                default:
                    return '[NewValue]'
            }
        })()

        const rules = [
            nsRuleset.generateRule({
                attribute: 'EntryType',
                argumentoperator: 'Er lik',
                argument: ns.EventDefinitions[this.event()].type,
                order: 1,
            }),
            nsRuleset.generateRule((this.data_systemRole
                    ? {
                        argument: this.data_systemRole.id,
                        argumentoperator: 'Er lik',
                        attribute: targetValue,
                        booleanoperator: 'AndAnd',
                        order: 2,
                    }
                    : {
                        argument: this.data_system.id,
                        argumentoperator: 'Er lik',
                        attribute: `[SYSTEMROLE;${targetValue};SystemId]`,
                        booleanoperator: 'AndAnd',
                        order: 2,
                    }
            ))
        ];

        return nsRuleset.generateRuleset({
            name: this.getLabel('ruleset.name'),
            description: this.getLabel('ruleset.description'),
            rules: rules,
        })
    }

    private getTranslationPrefix(): TTranslationKeyPrefix {
        let ret = 'permission'
        ret += '.'
        ret += ns.EventDefinitions[this.event()].key
        ret += '.'

        if (this.data_systemRole) {
            ret += 'systemrole'
        } else {
            ret += 'system'
        }

        return ret as TTranslationKeyPrefix
    }

    private getStepInfo = ko.pureComputed(() => {
        const translationObj = this.getTranslationObj()
        const step = this.step()
        if (step != null && step >= Step.Summary) {
            translationObj.ruleset = this.getSubmitData_ruleset()
            translationObj.messageflow = this.getSubmitData_messageFlow()
        }
        const key = 'permission.generate_mf_rs.wizard.info.' + step;
        if (lm.hasTranslation(key)) {
            return {
                k: key,
                br: true,
                obj: translationObj,
            }
        }
        return null
    })

    private getHeading = ko.pureComputed(() => {
        const translationObj = this.getTranslationObj()
        const step = this.step()
        const key = 'permission.generate_mf_rs.wizard.heading.' + step;
        if (lm.hasTranslation(key)) {
            return {
                k: key,
                br: true,
                obj: translationObj,
            }
        }
        return null
    })

    private getTranslationObj() {
        let importformat: 'visma' | 'vismapayroll' | 'other'
        switch (global.login.selectedOrganization()?.importformat?.id) {
            case ImportFormatId.Visma: {
                importformat = 'visma'
                break
            }
            case ImportFormatId.Visma_Employee_HR: {
                importformat = 'vismapayroll'
                break
            }
            default: {
                importformat = 'other'
                break
            }
        }
        return {
            event: ns.EventDefinitions[this.event()].key,
            importformat: importformat,
            messageflow: this.createMessageFlow.data ?? {},
            recipient: ns.RecipientDefinitions[this.recipient()].key,
            recipienttype: this.recipient() ? lm.t(ns.RecipientDefinitions[this.recipient()].label) : 'N/A',
            ruleset: this.createRuleset.data ?? {},
            system: this.data_system,
            systemrole: this.data_systemRole,
        };
    }

    dispose() {
        this.kosub.unsuscribeAll()
        this.req_createmessage.abort()
        this.req_createruleset.abort()
        this.req_getcompany.abort()
        this.req_getcompanysyncsteps.abort()
        this.req_getmessagetypes.abort()
        this.req_searchsystem.abort()
    }

}